/**
 * Badge Component - Small status indicators
 *
 * Variants:
 * - default: Neutral badge
 * - primary: Primary color badge
 * - success: Success badge
 * - warning: Warning badge
 * - error: Error badge
 *
 * Sizes:
 * - sm: Small badge
 * - md: Medium badge (default)
 * - lg: Large badge
 */

import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/shared/lib/utils';

const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-full font-medium transition-colors duration-200',
  {
    variants: {
      variant: {
        default: 'bg-neutral-100 text-neutral-700',
        primary: 'bg-primary-100 text-primary-700',
        success: 'bg-success-100 text-success-700',
        warning: 'bg-warning-100 text-warning-700',
        error: 'bg-error-100 text-error-700',
        info: 'bg-info-100 text-info-700',
      },
      size: {
        xs: 'h-4 px-1.5 text-[10px] font-medium',
        sm: 'h-5 px-2 text-xs',
        md: 'h-6 px-2.5 text-xs',
        lg: 'h-7 px-3 text-sm',
      },
      dot: {
        true: 'relative',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
      dot: false,
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  dot?: boolean;
}

/**
 * Badge Component
 *
 * @example
 * ```tsx
 * <Badge>Default</Badge>
 * <Badge variant="success">Success</Badge>
 * <Badge variant="error" size="lg">Error</Badge>
 * <Badge dot>Notification</Badge>
 * ```
 */
const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  ({ className, variant, size, dot, children, ...props }, ref) => {
    return (
      <div ref={ref} className={cn(badgeVariants({ variant, size, dot }), className)} {...props}>
        {dot && <span className="absolute -right-1 -top-1 flex h-2 w-2">
          <span className={cn(
            'absolute inline-flex h-full w-full animate-ping rounded-full opacity-75',
            {
              'bg-primary-500': variant === 'primary',
              'bg-success-500': variant === 'success',
              'bg-warning-500': variant === 'warning',
              'bg-error-500': variant === 'error',
              'bg-info-500': variant === 'info',
              'bg-neutral-500': variant === 'default',
            }
          )} />
          <span className={cn(
            'relative inline-flex rounded-full h-2 w-2',
            {
              'bg-primary-500': variant === 'primary',
              'bg-success-500': variant === 'success',
              'bg-warning-500': variant === 'warning',
              'bg-error-500': variant === 'error',
              'bg-info-500': variant === 'info',
              'bg-neutral-500': variant === 'default',
            }
          )} />
        </span>}
        {children}
      </div>
    );
  }
);

Badge.displayName = 'Badge';

export { Badge, badgeVariants };
export default Badge;
